import { useLazyQuery } from "@apollo/client";
import { gql } from "generated-graphql";
import { useCallback } from "react";

import { ManifestFormValues } from "../types";

export const DOCUMENT_MANIFEST_REVIEW_VALIDATOR = gql(`
  query DocumentManifestReviewValidator(
    $wasteManifestId: ID!
    $input: DocumentManifestEditInput!
  ) {
    documentManifestReviewValidator(wasteManifestId: $wasteManifestId, input: $input) {
      ...issue
    }
  }
`);

/**
 * Validator for the manifest review form's EPA IDs, for use with
 * `useValidatingForm`. Maps the form values to the document-manifest edit input
 * and returns the compliance-engine issues (keyed to the form fields). `no-cache`
 * so a re-typed value always re-checks against current handler/tenant data.
 *
 * On-change validation only; the initial-load issues come from
 * `DocumentManifestDetail.issues`.
 */
export function useDocumentManifestReviewValidation(wasteManifestId: string | null) {
  const [runValidation] = useLazyQuery(DOCUMENT_MANIFEST_REVIEW_VALIDATOR, {
    fetchPolicy: "no-cache",
  });

  return useCallback(
    async (values: ManifestFormValues) => {
      if (!wasteManifestId) return [];
      // Awaited execute with no-cache resolves with the fresh result, so `data`
      // is populated; `previousData` on the resolved result is deprecated.
      const { data } = await runValidation({
        variables: {
          wasteManifestId,
          input: {
            manifestTrackingNumber: values.manifestTrackingNumber || null,
            generatorId: values.generatorId || null,
            facilityId: values.facilityId || null,
            desFacilityId: values.desFacilityId || null,
            transporters: values.transporters.map((t) => ({
              transporterLineNumber: t.transporterLineNumber,
              transporterId: t.transporterId || null,
            })),
          },
        },
      });
      return data?.documentManifestReviewValidator ?? [];
    },
    [runValidation, wasteManifestId]
  );
}
